/**
 * WebSocket Client Service
 *
 * Manages WebSocket connection for real-time updates including:
 * - Execution status updates
 * - Team execution updates
 * - Agent status changes
 * - Error notifications
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Event subscription management
 * - Connection state tracking
 * - Message queuing during disconnection
 */

export type WebSocketEventType =
  | 'execution.update'
  | 'team_execution.update'
  | 'agent.update'
  | 'notification'
  | 'error'
  | 'connected'
  | 'subscribed'
  | 'unsubscribed'
  | 'pong'

export interface WebSocketMessage {
  type: WebSocketEventType | string
  [key: string]: any
}

export type WebSocketEventHandler = (data: any) => void

export enum ConnectionState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  CLOSED = 'CLOSED',
}

class WebSocketClient {
  private ws: WebSocket | null = null
  private url: string
  private token: string | null = null
  private userId: string | null = null

  // Connection state
  private state: ConnectionState = ConnectionState.DISCONNECTED
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private reconnectTimeout: number | null = null
  private baseReconnectDelay = 1000 // 1 second
  private maxReconnectDelay = 30000 // 30 seconds

  // Event handlers
  private eventHandlers: Map<string, Set<WebSocketEventHandler>> = new Map()
  private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set()

  // Message queue for when disconnected
  private messageQueue: WebSocketMessage[] = []
  private maxQueueSize = 100

  // Heartbeat
  private heartbeatInterval: number | null = null
  private heartbeatIntervalMs = 30000 // 30 seconds
  private heartbeatTimeoutId: number | null = null
  private missedHeartbeats = 0
  private maxMissedHeartbeats = 3

  constructor() {
    // Use environment variable or derive from current location (same-origin)
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const envUrl = import.meta.env.VITE_API_URL
    const wsHost = envUrl
      ? envUrl.replace('http://', '').replace('https://', '')
      : window.location.host
    this.url = `${wsProtocol}//${wsHost}/api/v1/ws`
  }

  /**
   * Connect to WebSocket server
   */
  connect(token: string, userId: string): void {
    if (this.state === ConnectionState.CONNECTED || this.state === ConnectionState.CONNECTING) {
      console.warn('WebSocket already connected or connecting')
      return
    }

    this.token = token
    this.userId = userId
    this.setState(ConnectionState.CONNECTING)

    try {
      // Create WebSocket connection with token as query param
      this.ws = new WebSocket(`${this.url}?token=${token}`)

      this.ws.onopen = () => {
        console.log('WebSocket connected')

        // Send authentication message
        this.send({
          type: 'auth',
          user_id: userId,
        })
      }

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          this.handleMessage(message)
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error)
      }

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason)
        this.handleDisconnect()
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      this.handleDisconnect()
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.setState(ConnectionState.CLOSED)
    this.stopHeartbeat()
    this.clearReconnectTimeout()

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
  }

  /**
   * Send a message to the server
   */
  send(message: WebSocketMessage): void {
    if (this.state === ConnectionState.CONNECTED && this.ws) {
      try {
        this.ws.send(JSON.stringify(message))
      } catch (error) {
        console.error('Failed to send WebSocket message:', error)
        this.queueMessage(message)
      }
    } else {
      this.queueMessage(message)
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(channel: string): void {
    this.send({
      type: 'subscribe',
      channel,
    })
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel: string): void {
    this.send({
      type: 'unsubscribe',
      channel,
    })
  }

  /**
   * Add event handler for specific event type
   */
  on(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }
    this.eventHandlers.get(eventType)!.add(handler)
  }

  /**
   * Remove event handler
   */
  off(eventType: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType)
    if (handlers) {
      handlers.delete(handler)
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType)
      }
    }
  }

  /**
   * Add connection state change handler
   */
  onStateChange(handler: (state: ConnectionState) => void): void {
    this.stateChangeHandlers.add(handler)
  }

  /**
   * Remove connection state change handler
   */
  offStateChange(handler: (state: ConnectionState) => void): void {
    this.stateChangeHandlers.delete(handler)
  }

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.state === ConnectionState.CONNECTED
  }

  // Private methods

  private handleMessage(message: WebSocketMessage): void {
    const { type } = message

    // Handle connection confirmation
    if (type === 'connected') {
      this.setState(ConnectionState.CONNECTED)
      this.reconnectAttempts = 0
      this.startHeartbeat()
      this.flushMessageQueue()
    }

    // Handle pong (heartbeat response)
    if (type === 'pong') {
      this.missedHeartbeats = 0
      if (this.heartbeatTimeoutId) {
        clearTimeout(this.heartbeatTimeoutId)
        this.heartbeatTimeoutId = null
      }
    }

    // Emit event to registered handlers
    const handlers = this.eventHandlers.get(type)
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          console.error(`Error in event handler for ${type}:`, error)
        }
      })
    }

    // Emit to wildcard handlers (*)
    const wildcardHandlers = this.eventHandlers.get('*')
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          console.error('Error in wildcard event handler:', error)
        }
      })
    }
  }

  private handleDisconnect(): void {
    this.stopHeartbeat()

    if (this.state === ConnectionState.CLOSED) {
      return // Intentional disconnect, don't reconnect
    }

    this.setState(ConnectionState.DISCONNECTED)
    this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      this.setState(ConnectionState.CLOSED)
      return
    }

    this.setState(ConnectionState.RECONNECTING)
    this.reconnectAttempts++

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    )
    const jitter = Math.random() * 1000 // Add up to 1 second of jitter

    console.log(`Reconnecting in ${delay + jitter}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)

    this.reconnectTimeout = setTimeout(() => {
      if (this.token && this.userId) {
        this.connect(this.token, this.userId)
      }
    }, delay + jitter)
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  private setState(state: ConnectionState): void {
    if (this.state !== state) {
      this.state = state
      console.log('WebSocket state changed:', state)

      // Notify state change handlers
      this.stateChangeHandlers.forEach((handler) => {
        try {
          handler(state)
        } catch (error) {
          console.error('Error in state change handler:', error)
        }
      })
    }
  }

  private queueMessage(message: WebSocketMessage): void {
    if (this.messageQueue.length >= this.maxQueueSize) {
      console.warn('Message queue full, dropping oldest message')
      this.messageQueue.shift()
    }
    this.messageQueue.push(message)
  }

  private flushMessageQueue(): void {
    console.log(`Flushing ${this.messageQueue.length} queued messages`)

    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift()
      if (message) {
        this.send(message)
      }
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()

    this.heartbeatInterval = setInterval(() => {
      if (this.state === ConnectionState.CONNECTED) {
        this.send({ type: 'ping' })

        // Set timeout to detect missed heartbeat
        this.heartbeatTimeoutId = setTimeout(() => {
          this.missedHeartbeats++
          console.warn(`Missed heartbeat (${this.missedHeartbeats}/${this.maxMissedHeartbeats})`)

          if (this.missedHeartbeats >= this.maxMissedHeartbeats) {
            console.error('Too many missed heartbeats, reconnecting')
            this.handleDisconnect()
          }
        }, 5000) // 5 second timeout for pong response
      }
    }, this.heartbeatIntervalMs)
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.heartbeatTimeoutId !== null) {
      clearTimeout(this.heartbeatTimeoutId)
      this.heartbeatTimeoutId = null
    }

    this.missedHeartbeats = 0
  }
}

// Export singleton instance
export const websocketClient = new WebSocketClient()
export default websocketClient
